import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AiOutlineSearch, AiOutlineUser } from "react-icons/ai";
import { GiTicket } from "react-icons/gi";
import { MdBusiness } from "react-icons/md";
import { HiOutlineBookOpen } from "react-icons/hi";
import toast from "react-hot-toast";
import api from "@/Api/Api";
import SearchResultGroup from "./SearchResultGroup";
import Searchbox from "../../Components/SearchBox/Searchbox";
import { SearchPageTabs } from "@shared/Enums/Enums";
import LoadingSpinner from "@shared/Components/LoadingSpinner";
import { Search } from "@shared/Entities/EntityTypes";

export default function SearchPage() {
	document.title = "Search | GoDesk";

	const params = useParams();
	const navigate = useNavigate();

	const [globalSearchResult, setGlobalSearchResult] = useState<Search>();
	const [searching, setSearching] = useState<boolean>(false);
	const [activeTab, setActiveTab] = useState<SearchPageTabs>();
	const [searchTerm, setSearchTerm] = useState<string>("");

	useEffect(() => {
		const query = params.query;

		if (query != null && query != "") {
			doGlobalSearch(query);
		}

		async function doGlobalSearch(searchTerm: string) {
			setSearching(true);
			setSearchTerm(searchTerm);

			const searchRes = await api.BasicGet<Search>("/search/global", { "query": searchTerm });

			setSearching(false);

			if (searchRes.successful && searchRes.data != null) {
				setGlobalSearchResult(searchRes.data);
			} else {
				toast.error("Could not fetch data. " + searchRes.errorCode + ": " + searchRes.errorMsg);
				setGlobalSearchResult(undefined);
			}
		}
	}, [params]);

	function searchOnChange(term: string) {
		navigate("/search/" + term);
	}

	function sumSearchResultTotals(result: Search | undefined): number {
		return (result?.agentsPagination?.total ?? 0) +
		(result?.companiesPagination?.total ?? 0) +
		(result?.ticketsPagination?.total ?? 0) +
		(result?.usersPagination?.total ?? 0) +
		(result?.articlesPagination?.total ?? 0);
	}

	return (
		<div className="mx-auto p-4 max-w-6xl">
			<div>
				<div className="text-2xl my-1">Search</div>
				<div className="my-3">
					Search tickets, users, companies, agents and KB articles.
				</div>
			</div>

			<div className="flex justify-between">
				<div className="flex items-center">
					<Searchbox searchCallback={searchOnChange} />
					<button className="btn-grey"><AiOutlineSearch /></button>
				</div>
			</div>

			{searching ?
				<LoadingSpinner />
				:
				<>
					<div className="border-b border-gray-300 flex justify-between">
						<div className="mt-3">
							{sumSearchResultTotals(globalSearchResult)} results found in {globalSearchResult?.timeTakenMs} ms.
						</div>
						<div>
							<SearchPageTab tabType={SearchPageTabs.TICKET} activeTab={activeTab} ticketCount={globalSearchResult?.ticketsPagination?.total ?? 0} onClick={setActiveTab} />
							<SearchPageTab tabType={SearchPageTabs.USER} activeTab={activeTab} ticketCount={globalSearchResult?.usersPagination?.total ?? 0} onClick={setActiveTab} />
							<SearchPageTab tabType={SearchPageTabs.COMPANY} activeTab={activeTab} ticketCount={globalSearchResult?.companiesPagination?.total ?? 0} onClick={setActiveTab} />
							<SearchPageTab tabType={SearchPageTabs.AGENT} activeTab={activeTab} ticketCount={globalSearchResult?.agentsPagination?.total ?? 0} onClick={setActiveTab} />
							<SearchPageTab tabType={SearchPageTabs.KBARTICLE} activeTab={activeTab} ticketCount={globalSearchResult?.articlesPagination?.total ?? 0} onClick={setActiveTab} />
						</div>
					</div>

					<div>
						{
							activeTab != null ?
								<SearchResultGroup defaultData={globalSearchResult} searchTerm={searchTerm} tabType={activeTab} />
								:
								<>
									<SearchResultGroup defaultData={globalSearchResult} searchTerm={searchTerm} tabType={SearchPageTabs.TICKET} />
									<SearchResultGroup defaultData={globalSearchResult} searchTerm={searchTerm} tabType={SearchPageTabs.USER} />
									<SearchResultGroup defaultData={globalSearchResult} searchTerm={searchTerm} tabType={SearchPageTabs.COMPANY} />
									<SearchResultGroup defaultData={globalSearchResult} searchTerm={searchTerm} tabType={SearchPageTabs.AGENT} />
									<SearchResultGroup defaultData={globalSearchResult} searchTerm={searchTerm} tabType={SearchPageTabs.KBARTICLE} />
								</>
						}
					</div>
				</>
			}
		</div>
	);
}

interface SearchPageTabProps {
	tabType: SearchPageTabs;
	activeTab?: SearchPageTabs;
	ticketCount: number;
	onClick(tabType: SearchPageTabs | undefined): void;
}

function SearchPageTab(props: SearchPageTabProps) {
	let tabIcon = <></>;
	let tabTitle = "";
	let tabStyles = {};

	switch (props.tabType) {
		case SearchPageTabs.AGENT:
			tabIcon = <AiOutlineUser />;
			tabTitle = "Agents";
			break;
		case SearchPageTabs.COMPANY:
			tabIcon = <MdBusiness />;
			tabTitle = "Company";
			break;
		case SearchPageTabs.TICKET:
			tabIcon = <GiTicket />;
			tabTitle = "Tickets";
			break;
		case SearchPageTabs.USER:
			tabIcon = <AiOutlineUser />;
			tabTitle = "Users";
			break;
		case SearchPageTabs.KBARTICLE:
			tabIcon = <HiOutlineBookOpen />;
			tabTitle = "KB Articles";
			break;
	}

	tabStyles = props.tabType == props.activeTab ? { backgroundColor: "#ddd" } : {};

	function onClick() {
		if (props.activeTab == props.tabType) {
			props.onClick(undefined);
		} else {
			props.onClick(props.tabType);
		}
	}

	return (
		<button className="btn-grey rounded-b-none border-b-0" title={tabTitle} style={tabStyles} onClick={onClick} >
			{tabIcon}
			{props.ticketCount}
		</button>
	);
}
